/**
 * ChannelLogo Component
 * 
 * Unified component for displaying channel logos (Msool, Airbnb, Booking, Gathern)
 * with standardized sizes and styling options.
 */

import { FC, memo, useMemo } from 'react';
import styled from 'styled-components';
import { IChannelLogoProps, LogoSize } from './types';
import { LOGO_SIZES, CHANNEL_BACKGROUNDS, CHANNEL_LIGHT_BACKGROUNDS, BORDER_RADIUS } from './constants';

// Import unified channel SVGs from /channels folder
import AirbnbIcon from '@assets/svg/channels/airbnb.svg?react';
import BookingIcon from '@assets/svg/channels/booking.svg?react';
import GathernIcon from '@assets/svg/channels/gathren.svg?react';
import MsoolIcon from '@assets/svg/MsoolIcon.svg?react';

// Import as image URLs (for img tag usage at smaller sizes)
import airbnbLogo from '@assets/svg/channels/airbnb.svg';
import bookingLogo from '@assets/svg/channels/booking.svg';
import gathernLogo from '@assets/svg/channels/gathren.svg';
import msoolLogo from '@assets/svg/MsoolIcon.svg';

interface ILogoWrapperProps {
  $size: number;
  $rounded: boolean;
  $disabled: boolean;
  $showBackground: boolean;
  $backgroundColor?: string;
}

const LogoWrapper = styled.div<ILogoWrapperProps>`
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  border-radius: ${(props) => props.$rounded ? BORDER_RADIUS.rounded : BORDER_RADIUS.default};
  background: ${(props) => props.$showBackground ? (props.$backgroundColor || 'transparent') : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  opacity: ${(props) => props.$disabled ? 0.4 : 1};
  filter: ${(props) => props.$disabled ? 'grayscale(100%)' : 'none'};
  transition: opacity 0.2s ease, filter 0.2s ease;

  svg, img {
    width: ${(props) => props.$showBackground ? `${props.$size * 0.6}px` : `${props.$size}px`};
    height: ${(props) => props.$showBackground ? `${props.$size * 0.6}px` : `${props.$size}px`};
    object-fit: contain;
  }
`;

/** Get numeric size from LogoSize or number */
const getSize = (size: LogoSize | number): number => {
  if (typeof size === 'number') return size;
  return LOGO_SIZES[size];
};

const ChannelLogo: FC<IChannelLogoProps> = memo(({
  channel,
  size = 'sm',
  rounded = false,
  disabled = false,
  showBackground = false,
  backgroundColor,
  className,
}) => {
  const numericSize = useMemo(() => getSize(size), [size]);
  
  const bgColor = useMemo(() => {
    if (backgroundColor) return backgroundColor;
    if (showBackground) return CHANNEL_LIGHT_BACKGROUNDS[channel];
    return 'transparent';
  }, [backgroundColor, showBackground, channel]);

  const renderLogo = () => {
    // Use React components for better quality at larger sizes
    // Use img tags for smaller sizes where simpler rendering is fine
    const useReactComponent = numericSize >= LOGO_SIZES.lg;
    
    switch (channel) {
      case 'msool':
        return useReactComponent ? <MsoolIcon /> : <img src={msoolLogo} alt="Msool" />;
      case 'airbnb':
        return useReactComponent ? <AirbnbIcon /> : <img src={airbnbLogo} alt="Airbnb" />;
      case 'booking':
        return useReactComponent ? <BookingIcon /> : <img src={bookingLogo} alt="Booking" />;
      case 'gathern':
        return useReactComponent ? <GathernIcon /> : <img src={gathernLogo} alt="Gathern" />;
      default:
        return null;
    }
  };

  return (
    <LogoWrapper
      $size={numericSize}
      $rounded={rounded}
      $disabled={disabled}
      $showBackground={showBackground}
      $backgroundColor={bgColor}
      className={className}
    >
      {renderLogo()}
    </LogoWrapper>
  );
});

ChannelLogo.displayName = 'ChannelLogo';

export default ChannelLogo;
